import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  AbstractComponent,
  Channel,
  ChannelService,
  WALLET_TYPE_LABEL_KEY,
  WalletService,
  WalletSummary,
  WalletTypeEnum,
  WalletUnitEnum,
  walletTypeForChannel,
} from 'lib-trend-core';
import { catchError, forkJoin, of, takeUntil } from 'rxjs';

export interface CampaignWalletChip {
  type: WalletTypeEnum;
  labelKey: string;
  balance: number;
  dotClass: string;
}

/**
 * The balance chips beside the title of the campaign list, the campaign detail
 * and the wizard: one per monetary wallet a dispatch of this company can be
 * charged to.
 *
 * A family is listed only when some channel of the company consumes from it, so
 * the answer follows the channels rather than the wallets: RCS with an RCS
 * channel, WhatsApp with a credit-line Cloud API channel
 * (SPEC-2026-10-whatsapp-wallet-credit-line-channels RF-31). An ordinary Cloud
 * API channel resolves to no wallet and adds no chip.
 */
@Component({
    selector: 'campaign-wallet',
    templateUrl: './campaign-wallet.component.html',
    styleUrls: ['./campaign-wallet.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CampaignWalletComponent extends AbstractComponent implements OnInit {

  /**
   * Left to right: RCS first, then WhatsApp (RF-31). Stated here because the
   * summary endpoint answers in enum order, which puts WhatsApp first — the
   * chips must not move when that order does. A family missing from this list
   * goes after the ones in it, in the order the server sent it.
   */
  private static readonly DISPLAY_ORDER: ReadonlyArray<WalletTypeEnum> = [
    WalletTypeEnum.RCS,
    WalletTypeEnum.WHATSAPP,
  ];

  wallets: Array<CampaignWalletChip> = [];

  constructor(
    injector: Injector,
    private walletService: WalletService,
    private channelService: ChannelService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.loadWallets();
  }

  private loadWallets(): void {
    forkJoin({
      summaries: this.walletService.getSummary().pipe(catchError(() => of<Array<WalletSummary>>([]))),
      channels: this.channelService.getList().pipe(catchError(() => of<Array<Channel>>([]))),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ summaries, channels }) => {
        const active = new Set(
          (channels ?? [])
            .map((channel) => walletTypeForChannel(channel))
            .filter((type): type is WalletTypeEnum => !!type),
        );

        this.wallets = (summaries ?? [])
          .filter((wallet) => active.has(wallet.type) && wallet.unit === WalletUnitEnum.BRL)
          // Array.prototype.sort is stable, so two families of the same rank keep
          // the order they arrived in.
          .sort((a, b) => this.displayRank(a.type) - this.displayRank(b.type))
          .map((wallet) => ({
            type: wallet.type,
            labelKey: WALLET_TYPE_LABEL_KEY[wallet.type],
            balance: wallet.balance ?? 0,
            dotClass: this.dotClass(wallet),
          }));
      });
  }

  private displayRank(type: WalletTypeEnum): number {
    const rank = CampaignWalletComponent.DISPLAY_ORDER.indexOf(type);
    return rank === -1 ? CampaignWalletComponent.DISPLAY_ORDER.length : rank;
  }

  private dotClass(wallet: WalletSummary): string {
    if (wallet.blocked) return 'is-blocked';
    return wallet.lowBalance ? 'is-low' : 'is-ok';
  }

}
